// frontend/src/features/onboarding/steps/CapabilityStep.tsx
//
// Wizard step 2 — Capability inventory (Task 17).
//
// Two plain-language questions on one screen (proposal §3.1):
//   1. "Do any of these run on your tank?" — multi-select brand cards from
//      DEVICE_TYPE_OPTIONS PLUS an EQUALLY-WEIGHTED "Just me and my test kits"
//      card. Selecting test-kits clears the brand picks and vice versa (mutual
//      exclusion both directions) — most reefers are proud manual testers, so
//      "just test kits" is a first-class identity, not a skip link.
//   2. "Do you dose?" — By hand / With a doser / No. This is the input the
//      dosing nav rule (§3.3) needs; the brand grid alone can't capture manual
//      dosers.
//
// The selectable-card affordance follows the repo idiom (features/tasks/
// DayPicker.tsx): an UnstyledButton with aria-pressed + data-selected, so the
// cards are keyboard-accessible and toggle-semantic.
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Group,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { useTank } from '@/contexts/TankContext';
import { useOnboarding } from '@/hooks/useOnboarding';
import { api } from '@/api/client';
import { tankParametersQueryKey } from '@/api/parameters';
import { DEVICE_TYPE_OPTIONS } from '@/features/devices/constants';
import { trackOnboarding } from '@/lib/onboardingAnalytics';
import type { DeviceType } from '@/types/devices';
import type { DosingAnswer } from '../types';
import { useOnboardingWizard } from '../WizardContext';

const DOSING_OPTIONS: { value: DosingAnswer; label: string }[] = [
  { value: 'manual', label: 'By hand' },
  { value: 'doser', label: 'With a doser' },
  { value: 'no', label: 'No' },
];

interface SelectableCardProps {
  title: string;
  description?: string;
  selected: boolean;
  onClick: () => void;
}

/**
 * A single selectable capability card. Rendered as a toggle button
 * (aria-pressed) so it is keyboard-accessible and screen-reader friendly.
 */
function SelectableCard({ title, description, selected, onClick }: SelectableCardProps) {
  return (
    <UnstyledButton
      onClick={onClick}
      aria-pressed={selected}
      aria-label={title}
      data-selected={selected || undefined}
      style={{
        display: 'block',
        width: '100%',
        padding: 'var(--mantine-spacing-md)',
        borderRadius: 'var(--mantine-radius-md)',
        border: '2px solid',
        borderColor: selected
          ? 'var(--mantine-color-teal-6)'
          : 'var(--mantine-color-default-border)',
        backgroundColor: selected ? 'var(--mantine-primary-color-light)' : 'transparent',
        textAlign: 'left',
        transition: 'border-color 150ms ease, background-color 150ms ease',
      }}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
        <Stack gap={2}>
          <Text fw={600}>{title}</Text>
          {description && (
            <Text size="sm" c="dimmed">
              {description}
            </Text>
          )}
        </Stack>
        {selected && <IconCheck size={18} color="var(--mantine-color-teal-6)" />}
      </Group>
    </UnstyledButton>
  );
}

export function CapabilityStep() {
  const { selectedTank } = useTank();
  const { update } = useOnboarding();
  const { setField, next, back } = useOnboardingWizard();
  const queryClient = useQueryClient();

  const [selectedBrands, setSelectedBrands] = useState<DeviceType[]>([]);
  const [testKitsOnly, setTestKitsOnly] = useState(false);
  const [dosing, setDosing] = useState<DosingAnswer | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleBrand = (brand: DeviceType) => {
    // Selecting any brand clears the mutually-exclusive test-kits card.
    setTestKitsOnly(false);
    setSelectedBrands((prev) =>
      prev.includes(brand) ? prev.filter((b) => b !== brand) : [...prev, brand]
    );
  };

  const selectTestKits = () => {
    // Selecting test-kits clears all brand selections (mutual exclusion).
    setTestKitsOnly((prev) => !prev);
    setSelectedBrands([]);
  };

  const q1Answered = testKitsOnly || selectedBrands.length > 0;
  const q2Answered = dosing !== null;
  const bothAnswered = q1Answered && q2Answered;

  const handleContinue = async () => {
    if (!bothAnswered || isSubmitting || dosing === null) return;

    setError(null);
    const brands = testKitsOnly ? [] : selectedBrands;

    // Store the capability answers in wizard state for the branch step (Task 18).
    setField({ selectedBrands: brands, dosing });

    // Dosing opt-in is capability-critical for nav visibility (has_doser), so the
    // write is awaited: a failure surfaces an Alert and does NOT advance. The user
    // retries simply by pressing Continue again.
    if (dosing !== 'no' && selectedTank) {
      setIsSubmitting(true);
      try {
        await api.put(`/api/tanks/${selectedTank.id}`, { dosing_opt_in: true });
        // Invalidate the capability-flag cache so nav visibility (has_doser)
        // reflects the new dosing_opt_in immediately (repo idiom, see
        // useAddDeviceFlow.ts).
        queryClient.invalidateQueries({ queryKey: tankParametersQueryKey(selectedTank.id) });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save your dosing setting');
        setIsSubmitting(false);
        return;
      }
      setIsSubmitting(false);
    }

    // Funnel: both questions answered and any dosing write succeeded — the
    // capability step is complete.
    trackOnboarding('step_completed', { step: 'capability' });

    // Persist wizard progress fire-and-forget — do not block navigation on it.
    update({ step: 2 }).catch(() => {});
    next();
  };

  return (
    <Stack gap="xl" data-testid="onboarding-step-capability">
      <Stack gap="xs">
        <Title order={1}>Tell us about your setup</Title>
        <Text c="dimmed">
          This tailors what you see — which pages appear and how your dashboard fills in. You
          can change any of it later.
        </Text>
      </Stack>

      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          onClose={() => setError(null)}
          withCloseButton
        >
          {error}
        </Alert>
      )}

      {/* Question 1 — brands (+ equally-weighted test-kits card). */}
      <Stack gap="sm" role="group" aria-labelledby="capability-brands-label">
        <Text fw={500} id="capability-brands-label">
          Do any of these run on your tank?
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
          {DEVICE_TYPE_OPTIONS.map((option) => (
            <SelectableCard
              key={option.value}
              title={option.label}
              selected={selectedBrands.includes(option.value as DeviceType)}
              onClick={() => toggleBrand(option.value as DeviceType)}
            />
          ))}
          <SelectableCard
            title="Just me and my test kits"
            description="No connected hardware — you log tests by hand"
            selected={testKitsOnly}
            onClick={selectTestKits}
          />
        </SimpleGrid>
      </Stack>

      {/* Question 2 — dosing. */}
      <Stack gap="sm" role="group" aria-labelledby="capability-dosing-label">
        <Text fw={500} id="capability-dosing-label">
          Do you dose?
        </Text>
        <SegmentedControl
          fullWidth
          aria-labelledby="capability-dosing-label"
          value={dosing ?? ''}
          onChange={(value) => setDosing(value as DosingAnswer)}
          data={DOSING_OPTIONS}
        />
      </Stack>

      <Group justify="space-between">
        <Button variant="subtle" onClick={back} disabled={isSubmitting}>
          Back
        </Button>
        <Button
          onClick={() => void handleContinue()}
          disabled={!bothAnswered}
          loading={isSubmitting}
        >
          Continue
        </Button>
      </Group>
    </Stack>
  );
}
